<template>
  <StyledButton
    class="btn ips-btn"
    :class="computedStyleClasses"
    :disabled="disabledState"
    :type="type"
    :size="size"
    :variant="variant"
    :tabindex="tabindex"
    :aria-disabled="disabled"
    :aria-label="computedAriaLabel"
    :aria-busy="loading"
    autocomplete="off"
    @click="onClick"
  >
    <ips-icon v-if="icon && !loading" :icon="icon" :style="computedIconStyle"></ips-icon>
    <ips-spinner v-if="loading" size="sm" class="pe-2" :text="loadingText"></ips-spinner>
    <span v-if="renderSlot"><slot></slot></span>
    <ips-icon
      v-if="iconAppend && !loading"
      :icon="iconAppend"
      class="ps-2"
      :class="{ 'float-end': iconFloatRight }"
    ></ips-icon>
  </StyledButton>
</template>

<script>
import StyledButton from './IpsButton';
import IpsIcon from '@ipscape/ips-icon';
import IpsSpinner from '@ipscape/ips-spinner';

export default {
  name: 'IpsButton',
  components: {
    IpsIcon,
    IpsSpinner,
    StyledButton,
  },
  props: {
    classes: {
      name: 'classes',
      description: 'The classes',
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    variant: {
      name: 'variant',
      description: 'The variant of the button',
      type: String,
      default: 'primary',
      required: false,
      validator: (value) =>
        ['', 'primary', 'secondary', 'tertiary', 'minimal', 'success', 'danger', 'link'].indexOf(
          value
        ) !== -1,
    },
    disabled: {
      name: 'disabled',
      description: 'Makes a button disabled if true',
      type: Boolean,
      default: false,
      required: false,
    },
    size: {
      name: 'size',
      description: 'The button size.',
      type: String,
      default: 'md',
      required: false,
      validator: (value) => ['', 'xxs', 'xs', 'sm', 'md', 'lg', 'block'].indexOf(value) !== -1,
    },
    type: {
      name: 'type',
      description: 'The type of the button',
      type: String,
      default: 'button',
      required: false,
      validator: (value) => ['', 'button', 'reset', 'submit'].indexOf(value) !== -1,
    },
    circle: {
      name: 'circle',
      description: 'Makes a circle button if true',
      type: Boolean,
      default: false,
      required: false,
    },
    icon: {
      name: 'icon',
      description: 'The icon prepend',
      type: String,
      default: null,
      required: false,
    },
    iconAppend: {
      name: 'iconAppend',
      description: 'The icon append',
      type: String,
      default: null,
      required: false,
    },
    ariaLabel: {
      name: 'ariaLabel',
      description: 'The aria label',
      type: String,
      default: '',
      required: false,
    },
    loading: {
      name: 'loading',
      description: 'Adds spinner to button and changes to disabled state',
      type: Boolean,
      default: false,
      required: false,
    },
    loadingText: {
      name: 'loadingText',
      description: 'Text to display next to the spinner while loading',
      type: String,
      default: 'Loading',
      required: false,
    },
    tabindex: {
      name: 'tabindex',
      description: 'Adds tab index to button',
      type: Number,
      default: null,
      required: false,
    },
  },
  computed: {
    computedVariant() {
      switch (this.variant) {
        case 'secondary':
          return 'btn-secondary';
        case 'tertiary':
          return 'btn-tertiary';
        case 'minimal':
          return 'btn-minimal';
        case 'success':
          return 'btn-success';
        case 'danger':
          return 'btn-danger';
        case 'link':
          return 'btn-link';
        case 'primary':
        default:
          return 'btn-primary';
      }
    },
    computedStyleClasses() {
      const classesArray = [];
      classesArray.push(this.computedVariant);
      classesArray.push(`btn-${this.size}`);
      if (this.classes.length > 0) {
        this.classes.forEach((item) => {
          classesArray.push(item);
        });
      }
      if (this.circle) classesArray.push(`btn-circle btn-circle-${this.size}`);
      return classesArray;
    },
    computedIconStyle() {
      return this.icon && !this.circle ? 'padding-right: 8px' : '';
    },
    computedAriaLabel() {
      return this.loading ? this.loadingText : this.ariaLabel;
    },
    iconFloatLeft() {
      return this.iconAppend;
    },
    iconFloatRight() {
      return this.icon;
    },
    disabledState() {
      return this.disabled || this.loading;
    },
    renderSlot() {
      return !this.loading;
    },
    spinnerPaddingRight() {
      return (this.loadingText || this.$slots.default) && this.loading && this.loadingText !== '';
    },
  },
  methods: {
    onClick(event) {
      this.$emit('click', event);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'IpsButton';
</style>
